import { useEffect, forwardRef } from 'react';
import { ChangeEvent, memo, useState, KeyboardEvent, useRef } from 'react';
import {
  format,
  isExists,
  isBefore,
  isAfter,
  toDate,
  isSameDay,
} from 'date-fns';
import { initDateTime } from 'utils';

import CalendarIcon from 'shared/icons/Calendar';

import clsx from 'clsx';
import { useStyles } from './style';

export interface ClassNames {
  root?: string;
  text?: string;
  icon?: string;
}

interface Props {
  date: Date | null;
  disabled?: boolean;
  handleChangeDate: (date: Date | null, name?: string) => void;
  handleToggleCalendar: () => void;
  minDate?: Date | null;
  maxDate?: Date | null;
  name?: string;
  placeholder?: string;
  variant: 'standard' | 'underline';
  cls?: ClassNames;
}

const DatePickerInput = forwardRef<HTMLDivElement, Props>(
  (
    {
      date,
      name,
      disabled,
      placeholder,
      maxDate,
      minDate,
      handleChangeDate,
      handleToggleCalendar,
      variant,
      cls,
    },
    ref
  ) => {
    const classes = useStyles();
    let inputRef = useRef<HTMLInputElement | null>(null);
    const [value, setValue] = useState(date ? format(date, 'd.M.yyyy') : '');

    useEffect(() => {
      setValue(date ? format(date, 'd.M.yyyy') : '');
    }, [date]);

    const handleChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
      let newValue = e.target.value;
      newValue = newValue.replace(/[^\d|.]/g, '');

      setValue(newValue);
    };

    const parseExistingDate = (year: number, month: number, day: number) => {
      let date = initDateTime(new Date(year, month, day)),
        initMaxDate = maxDate ? initDateTime(maxDate) : null,
        initMinDate = minDate ? initDateTime(minDate) : null;

      if (initMaxDate && isAfter(date, initMaxDate)) {
        date = toDate(initMaxDate);
        return date;
      }

      if (initMinDate && isBefore(date, initMinDate)) {
        date = toDate(initMinDate);
        return date;
      }

      return date;
    };

    const parse = () => {
      const pattern =
        /(0[1-9]|[1-9]|[12][0-9]|3[01])[/|.|-]([1-9]|0[1-9]|1[012])[/|.|-](19\d\d$|20\d\d$)/;

      let match = pattern.exec(value);

      if (match) {
        let [, dd, mm, yyyy] = match;
        let day = parseInt(dd),
          month = parseInt(mm) - 1,
          year = parseInt(yyyy);

        let newDate = initDateTime(new Date());

        if (isExists(year, month, day)) {
          newDate = parseExistingDate(year, month, day);

          if (date && isSameDay(newDate, date)) {
            setValue(format(date, 'd.M.yyyy'));
            return;
          }

          handleChangeDate(newDate, name);
        } else {
          setValue('');
          handleChangeDate(null, name);
        }
      } else {
        handleChangeDate(null, name);
        setValue('');
      }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        inputRef.current?.blur();
      }
    };

    return (
      <div
        className={clsx(classes.root, cls?.root, {
          [classes.underline]: variant === 'underline',
        })}
        onKeyDown={disabled ? undefined : handleKeyDown}
        ref={ref}
      >
        <input
          ref={inputRef}
          value={value}
          onChange={disabled ? undefined : handleChangeValue}
          onBlur={disabled ? undefined : parse}
          className={clsx(classes.input, cls?.text, {
            [classes.disabled]: disabled,
          })}
          placeholder={placeholder}
          disabled={disabled}
        />
        <CalendarIcon
          sx={{ fontSize: '1.2rem' }}
          className={clsx(classes.icon, cls?.icon, {
            [classes.disabledIcon]: disabled,
          })}
          onClick={disabled ? undefined : handleToggleCalendar}
        />
      </div>
    );
  }
);

export default memo(DatePickerInput);
